import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};

// Country code → app language
const COUNTRY_LANG = {
  IN: 'hi',                                                             // India → Hindi
  PK: 'ur', BD: 'ur',                                                  // Pakistan, Bangladesh → Urdu
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', CL: 'es',        // Spanish-speaking
  VE: 'es', EC: 'es', BO: 'es', UY: 'es', PY: 'es', CR: 'es',
  PA: 'es', DO: 'es', SV: 'es', HN: 'es', NI: 'es', GT: 'es', CU: 'es',
  FR: 'fr', BE: 'fr', SN: 'fr', CI: 'fr', ML: 'fr', CM: 'fr',        // French-speaking
  DZ: 'fr', TN: 'fr', MA: 'fr', CD: 'fr', MG: 'fr',
};

// Detect from browser locale as instant fallback (no network needed)
const getLangFromLocale = () => {
  const l = (navigator.language || navigator.languages?.[0] || 'en').toLowerCase();
  if (l.startsWith('hi')) return 'hi';
  if (l.startsWith('ur')) return 'ur';
  if (l.startsWith('es')) return 'es';
  if (l.startsWith('fr')) return 'fr';
  return 'en';
};

// Detect language: saved preference wins; otherwise IP geolocation → locale fallback
const detectLanguage = async () => {
  const saved = localStorage.getItem('shayari-lang');
  if (saved) return saved;

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3000);
    const res = await fetch('https://ip-api.com/json/?fields=countryCode', { signal: controller.signal });
    clearTimeout(timer);
    const { countryCode } = await res.json();
    return COUNTRY_LANG[countryCode] || 'en';
  } catch {
    return getLangFromLocale();
  }
};

export const AppProvider = ({ children }) => {
  // Start with saved lang or locale-based guess (instant, no flash)
  const [language, setLanguage] = useState(
    () => localStorage.getItem('shayari-lang') || getLangFromLocale()
  );
  const [theme, setTheme] = useState(() => localStorage.getItem('shayari-theme') || 'light');
  const [favorites, setFavorites]   = useState(() => JSON.parse(localStorage.getItem('shayari-fav') || '[]'));
  const [likes, setLikes]           = useState(() => JSON.parse(localStorage.getItem('shayari-likes') || '{}'));
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [toast, setToast]           = useState(null);

  // On first visit (no saved pref), fetch accurate IP-based country → language
  useEffect(() => {
    if (localStorage.getItem('shayari-lang')) return; // user has a saved choice — skip
    detectLanguage().then(lang => {
      setLanguage(lang);
      localStorage.setItem('shayari-lang', lang);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { localStorage.setItem('shayari-lang', language); }, [language]);
  useEffect(() => {
    localStorage.setItem('shayari-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  useEffect(() => { localStorage.setItem('shayari-fav', JSON.stringify(favorites)); }, [favorites]);
  useEffect(() => { localStorage.setItem('shayari-likes', JSON.stringify(likes)); }, [likes]);

  const toggleFavorite = useCallback((id) => {
    setFavorites(prev => {
      const isFav = prev.includes(id);
      // showToast called inside to avoid stale closure; dependency intentionally omitted
      setToast({ message: isFav ? '💔 Removed from favorites' : '❤️ Added to favorites', type: 'success', id: Date.now() });
      setTimeout(() => setToast(null), 2800);
      return isFav ? prev.filter(f => f !== id) : [...prev, id];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLike = useCallback((id) => {
    setLikes(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type, id: Date.now() });
    setTimeout(() => setToast(null), 2800);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const isFavorite = useCallback((id) => favorites.includes(id), [favorites]);
  const isLiked    = useCallback((id) => !!likes[id], [likes]);

  return (
    <AppContext.Provider value={{
      language, setLanguage,
      theme, toggleTheme,
      favorites, toggleFavorite, isFavorite,
      likes, toggleLike, isLiked,
      searchQuery, setSearchQuery,
      activeCategory, setActiveCategory,
      toast, showToast,
    }}>
      {children}
    </AppContext.Provider>
  );
};
